import { supabase } from "@/integrations/supabase/client";

export type Message = {
  id: string;
  sender_id: string;
  recipient_id: string;
  listing_id: string | null;
  body: string;
  read: boolean;
  created_at: string;
};

export type PeerProfile = {
  id: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
};

export type Conversation = {
  peerId: string;
  peer: PeerProfile | null;
  lastMessage: Message;
  unread: number;
};

export async function fetchProfilesByIds(ids: string[]): Promise<Map<string, PeerProfile>> {
  const map = new Map<string, PeerProfile>();
  const unique = [...new Set(ids)].filter(Boolean);
  if (!unique.length) return map;
  const results = await Promise.all(
    unique.map((id) => supabase.rpc("public_profile", { _id: id })),
  );
  results.forEach((res, i) => {
    const row = Array.isArray(res.data) ? res.data[0] : null;
    const id = unique[i]!;
    map.set(
      id,
      row
        ? {
            id,
            full_name: (row as PeerProfile).full_name,
            username: (row as PeerProfile).username,
            avatar_url: (row as PeerProfile).avatar_url,
          }
        : { id, full_name: null, username: null, avatar_url: null },
    );
  });
  return map;
}

export function peerName(p: PeerProfile | null | undefined) {
  return p?.full_name?.trim() || p?.username?.trim() || "İstifadəçi";
}

/** Cari istifadəçinin bütün yazışmalarını söhbətlərə qruplaşdırır. */
export async function fetchConversations(userId: string): Promise<Conversation[]> {
  const { data, error } = await supabase
    .from("messages")
    .select("*")
    .or(`sender_id.eq.${userId},recipient_id.eq.${userId}`)
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) throw error;
  const rows = (data ?? []) as Message[];
  const byPeer = new Map<string, Conversation>();
  for (const m of rows) {
    const peerId = m.sender_id === userId ? m.recipient_id : m.sender_id;
    const existing = byPeer.get(peerId);
    const unreadInc = m.recipient_id === userId && !m.read ? 1 : 0;
    if (!existing) byPeer.set(peerId, { peerId, peer: null, lastMessage: m, unread: unreadInc });
    else existing.unread += unreadInc;
  }
  const profiles = await fetchProfilesByIds([...byPeer.keys()]);
  for (const c of byPeer.values()) c.peer = profiles.get(c.peerId) ?? null;
  return [...byPeer.values()];
}

export async function fetchThread(userId: string, peerId: string): Promise<Message[]> {
  const { data, error } = await supabase
    .from("messages")
    .select("*")
    .or(
      `and(sender_id.eq.${userId},recipient_id.eq.${peerId}),and(sender_id.eq.${peerId},recipient_id.eq.${userId})`,
    )
    .order("created_at", { ascending: true })
    .limit(500);
  if (error) throw error;
  return (data ?? []) as Message[];
}

export async function sendMessage(input: {
  senderId: string;
  recipientId: string;
  body: string;
  listingId?: string | null;
}) {
  const { error } = await supabase.from("messages").insert({
    sender_id: input.senderId,
    recipient_id: input.recipientId,
    body: input.body.trim(),
    listing_id: input.listingId ?? null,
  });
  if (error) throw error;
}

export async function markThreadRead(userId: string, peerId: string) {
  await supabase
    .from("messages")
    .update({ read: true })
    .eq("recipient_id", userId)
    .eq("sender_id", peerId)
    .eq("read", false);
}

/* ------------ Admin ------------- */

export type AdminMessage = Message & {
  senderName: string;
  recipientName: string;
};

export async function fetchAllMessages(limit = 500): Promise<AdminMessage[]> {
  const { data, error } = await supabase
    .from("messages")
    .select("*")
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  const rows = (data ?? []) as Message[];
  const ids = rows.flatMap((m) => [m.sender_id, m.recipient_id]);
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, full_name, username")
    .in("id", [...new Set(ids)]);
  const map = new Map(
    (profiles ?? []).map((p) => [p.id, (p.full_name || p.username || "İstifadəçi") as string]),
  );
  return rows.map((m) => ({
    ...m,
    senderName: map.get(m.sender_id) ?? "İstifadəçi",
    recipientName: map.get(m.recipient_id) ?? "İstifadəçi",
  }));
}